// force redeploy
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  MoreHoriz as MoreHorizIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, IconButton, InputBase, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { BlockItem, BlockWidgetItem, WidgetConfig } from '@trinity/chat/types/dashboard-config';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useDashboardConfig,
  useDashboardWidgetData,
  useWidgetRegistry,
} from '@trinity/chat-components/containers/Dashboard/context';
import {
  BLOCK_GAP,
  MAX_PERMISSIBLE_ROW_SPAN,
  ROW_HEIGHT,
  SLOT_PADDING,
  getBlockRowHeightMap,
  getContentHeightUnits,
  getPreviewBlockRowHeightMap,
  getPreviewContentHeightUnits,
} from './dashboardLayoutUtils';
import { SectionCardShell } from './SectionCardShell';
import { ThemedMenu } from './shared-styled';
import { WidgetPalette } from './WidgetPalette';
import { useBuilderPalette } from './PaletteContext';

// --- Types --------------------------------------------------------------------

type Slot = number;

type Props = {
  blockId: string;
  title: string;
  /** Whether the block title is hidden in the rendered dashboard (shows in section-title style in the builder). */
  hideTitle: boolean;
  /** Number of grid columns this block spans */
  blockCols: number;
  /** Whether this block is currently collapsed to header-only height. Controlled externally. */
  collapsed: boolean;
  /** Toggle the collapsed state (controlled by BuilderCanvas). */
  onToggleCollapse: () => void;
  /** When true, renders in clean read-only preview mode (no editing chrome). */
  readOnly?: boolean;
  /** When true, the block can be collapsed in readOnly mode (accordion style). */
  collapsible?: boolean;
};

type DragData =
  | { kind: 'move'; blockId: string; row: number; slot: Slot }
  | { kind: 'resize'; blockId: string; row: number; slot: Slot }
  | { kind: 'section-move'; blockId: string; row: number; slot: Slot }
  | { kind: 'section-resize'; blockId: string; sectionId: string; row: number; slot: Slot }
  /** Widget dragged from within a section -- enables section->block and section->section drops */
  | { kind: 'section-widget-move'; blockId: string; sectionId: string; row: number; slot: Slot };

// --- Helpers ------------------------------------------------------------------

/** Maximum 1-indexed row used by any item in a block. Returns 1 if no items. */
function getMaxRow(items: BlockItem[]): number {
  if (items.length === 0) return 1;
  return Math.max(...items.map((item) => item.row ?? 1));
}

/**
 * Returns true when all column-slots in the given row are fully covered.
 */
function isRowFull(items: BlockItem[], row: number, cols: number): boolean {
  const rowItems = items.filter((item) => (item.row ?? 1) === row);
  let covered = 0;
  for (const item of rowItems) covered += item.span;
  return covered >= cols;
}

// --- Block Header sub-component ----------------------------------------------

type BlockHeaderProps = {
  readOnly: boolean;
  title: string;
  hideTitle: boolean;
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  blockMenuAnchor: null | HTMLElement;
  setBlockMenuAnchor: (el: null | HTMLElement) => void;
  blockMenuId: string;
  blockId: string;
  toggleBlockHideTitle: (id: string) => void;
  toggleBlockCollapsible: (id: string) => void;
  removeBlock: (id: string) => void;
};

function BlockHeader({
  readOnly,
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
  handleTitleChange,
  blockMenuAnchor,
  setBlockMenuAnchor,
  blockMenuId,
  blockId,
  toggleBlockHideTitle,
  toggleBlockCollapsible,
  removeBlock,
}: BlockHeaderProps): React.ReactElement | null {
  if (readOnly) {
    return renderReadOnlyHeader({ title, hideTitle, collapsible, collapsed, onToggleCollapse });
  }
  return (
    <BuilderHeader
      title={title}
      hideTitle={hideTitle}
      collapsible={collapsible}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      handleTitleChange={handleTitleChange}
      blockMenuAnchor={blockMenuAnchor}
      setBlockMenuAnchor={setBlockMenuAnchor}
      blockMenuId={blockMenuId}
      blockId={blockId}
      toggleBlockHideTitle={toggleBlockHideTitle}
      toggleBlockCollapsible={toggleBlockCollapsible}
      removeBlock={removeBlock}
    />
  );
}

function renderReadOnlyHeader({
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
}: Pick<
  BlockHeaderProps,
  'title' | 'hideTitle' | 'collapsible' | 'collapsed' | 'onToggleCollapse'
>): React.ReactElement | null {
  if (hideTitle || !title) return null;
  if (collapsible) {
    return (
      <Accordion
        expanded={!collapsed}
        onChange={() => onToggleCollapse()}
        sx={{
          border: 'none',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            width: '100%',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': { alignItems: 'center' },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
            '& .MuiAccordionSummary-expandIconWrapper': { alignSelf: 'center' },
          }}
        >
          <Typography
            fontSize="20px"
            sx={{ flexGrow: 1 }}
            fontFamily="var(--font-bold, inherit)"
            fontWeight={700}
            color="var(--black-l, #323232)"
          >
            {title}
          </Typography>
        </AccordionSummary>
      </Accordion>
    );
  }
  return (
    <PreviewBlockHeader>
      <Typography
        noWrap
        sx={{ flex: 1, fontSize: '20px', fontWeight: 700, fontFamily: 'var(--font-bold, inherit)' }}
      >
        {title}
      </Typography>
    </PreviewBlockHeader>
  );
}

function BuilderHeader({
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
  handleTitleChange,
  blockMenuAnchor,
  setBlockMenuAnchor,
  blockMenuId,
  blockId,
  toggleBlockHideTitle,
  toggleBlockCollapsible,
  removeBlock,
}: Omit<BlockHeaderProps, 'readOnly'>): React.ReactElement {
  const iconSize = hideTitle ? 12 : 16;
  const btnPadding = hideTitle ? '1px' : undefined;
  return (
    <ShellHeader className="drag-handle" collapsed={collapsed} hideTitle={hideTitle}>
      <TitleInput
        hideTitle={hideTitle}
        value={title}
        onChange={handleTitleChange}
        placeholder="Block name..."
        inputProps={{ 'aria-label': 'Block title' }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setBlockMenuAnchor(e.currentTarget);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Block actions"
        aria-controls={blockMenuAnchor ? blockMenuId : undefined}
        aria-haspopup="true"
        sx={{ flexShrink: 0, padding: btnPadding }}
      >
        <MoreHorizIcon fontSize={hideTitle ? 'inherit' : 'small'} />
      </IconButton>
      <ThemedMenu
        id={blockMenuId}
        anchorEl={blockMenuAnchor}
        open={Boolean(blockMenuAnchor)}
        onClose={() => setBlockMenuAnchor(null)}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            toggleBlockHideTitle(blockId);
          }}
        >
          {hideTitle ? 'Show title' : 'Hide title'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            toggleBlockCollapsible(blockId);
          }}
        >
          {collapsible ? 'Remove collapsible' : 'Make collapsible'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            removeBlock(blockId);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete block
        </MenuItem>
      </ThemedMenu>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={collapsed ? 'Expand block' : 'Collapse block'}
        sx={{ flexShrink: 0, ml: 0.25, padding: btnPadding }}
      >
        {collapsed ? <ChevronDown size={iconSize} /> : <ChevronUp size={iconSize} />}
      </IconButton>
    </ShellHeader>
  );
}

// --- Sub-row cell builder ----------------------------------------------------

type SubRowCellsParams = {
  blockRow: number;
  subRowIdx: number;
  primaryRow: number;
  secCols: Set<number>;
  items: BlockItem[];
  readOnly: boolean;
  blockCols: number;
  blockId: string;
  registry: { get: (type: string) => { noBorder?: boolean } | undefined };
  selectedWidgetId: string | null;
  dragOverTarget: { row: number; slot: Slot } | null;
  pendingSlot: { blockId: string; row: number; slot: Slot } | null;
  blockPrimaryAssigned: { value: boolean };
  handleMoveDragStart: (e: React.DragEvent, row: number, slot: Slot) => void;
  handleDragOver: (e: React.DragEvent, row: number, slot: Slot) => void;
  setDragOverTarget: (t: { row: number; slot: Slot } | null) => void;
  handleDrop: (e: React.DragEvent, row: number, slot: Slot) => void;
  handleResizeDragStart: (e: React.DragEvent, row: number, slot: Slot) => void;
  updateWidgetRowSpan: (blockId: string, row: number, slot: Slot, span: number) => void;
  setWidgetMenu: (v: { anchorEl: HTMLElement; row: number; slot: Slot } | null) => void;
  setPaletteTarget: (v: { row: number; slot: Slot } | null) => void;
};

function buildSubRowCells(params: SubRowCellsParams): React.ReactNode[] {
  const {
    blockRow,
    subRowIdx,
    primaryRow,
    secCols,
    items,
    readOnly,
    blockCols,
    blockId,
    registry,
    selectedWidgetId,
    dragOverTarget,
    pendingSlot,
    blockPrimaryAssigned,
    handleMoveDragStart,
    handleDragOver,
    setDragOverTarget,
    handleDrop,
    handleResizeDragStart,
    updateWidgetRowSpan,
    setWidgetMenu,
    setPaletteTarget,
  } = params;
  const cssRow = subRowIdx + 1;
  const rowNonSectionItems = items.filter((i) => (i.row ?? 1) === blockRow && i.type !== 'section');

  const coveredCols = computeCoveredCols(secCols, rowNonSectionItems, subRowIdx, items, primaryRow);

  const cells: React.ReactNode[] = buildWidgetCells({
    rowNonSectionItems,
    readOnly,
    blockRow,
    cssRow,
    blockId,
    registry,
    selectedWidgetId,
    dragOverTarget,
    handleMoveDragStart,
    handleDragOver,
    setDragOverTarget,
    handleDrop,
    handleResizeDragStart,
    updateWidgetRowSpan,
    setWidgetMenu,
  });

  if (!readOnly) {
    buildPlaceholderCells(cells, {
      blockRow,
      cssRow,
      blockCols,
      blockId,
      coveredCols,
      dragOverTarget,
      pendingSlot,
      blockPrimaryAssigned,
      handleDragOver,
      setDragOverTarget,
      handleDrop,
      setPaletteTarget,
    });
  }

  return cells;
}

function computeCoveredCols(
  secCols: Set<number>,
  rowNonSectionItems: BlockItem[],
  subRowIdx: number,
  items: BlockItem[],
  primaryRow: number
): Set<number> {
  const coveredCols = new Set<number>(secCols);
  for (const item of rowNonSectionItems) {
    for (let c = 0; c < item.span; c++) coveredCols.add(item.slot + c);
  }
  if (subRowIdx > 0) {
    for (const pItem of items) {
      if (pItem.type !== 'widget' || (pItem.row ?? 1) !== primaryRow) continue;
      const pRowSpan = pItem.rowSpan ?? 1;
      if (subRowIdx >= pRowSpan) continue;
      for (let c = pItem.slot; c < pItem.slot + pItem.span; c++) {
        coveredCols.add(c);
      }
    }
  }
  return coveredCols;
}

type WidgetCellsParams = {
  rowNonSectionItems: BlockItem[];
  readOnly: boolean;
  blockRow: number;
  cssRow: number;
  blockId: string;
  registry: { get: (type: string) => { noBorder?: boolean } | undefined };
  selectedWidgetId: string | null;
  dragOverTarget: { row: number; slot: Slot } | null;
  handleMoveDragStart: (e: React.DragEvent, row: number, slot: Slot) => void;
  handleDragOver: (e: React.DragEvent, row: number, slot: Slot) => void;
  setDragOverTarget: (t: { row: number; slot: Slot } | null) => void;
  handleDrop: (e: React.DragEvent, row: number, slot: Slot) => void;
  handleResizeDragStart: (e: React.DragEvent, row: number, slot: Slot) => void;
  updateWidgetRowSpan: (blockId: string, row: number, slot: Slot, span: number) => void;
  setWidgetMenu: (v: { anchorEl: HTMLElement; row: number; slot: Slot } | null) => void;
};

function buildWidgetCells(p: WidgetCellsParams): React.ReactNode[] {
  const cells: React.ReactNode[] = [];
  const dragRow = p.dragOverTarget?.row;
  const dragSlot = p.dragOverTarget?.slot;

  for (const item of p.rowNonSectionItems) {
    if (item.type !== 'widget') continue;
    const iSlot = item.slot;
    const itemRowSpan = item.rowSpan ?? 1;
    const widgetEntry = p.registry.get(item.widget.type);
    const noBorder = widgetEntry?.noBorder ?? false;
    cells.push(
      <WidgetSlotCell
        key={`${p.blockRow}-${iSlot}`}
        span={item.span}
        readOnly={p.readOnly}
        isEditing={!p.readOnly && p.selectedWidgetId === item.widget.id}
        noBorder={noBorder}
        {...(!p.readOnly && {
          draggable: true,
          onDragStart: (e: React.DragEvent) => p.handleMoveDragStart(e, p.blockRow, iSlot),
          onDragOver: (e: React.DragEvent) => p.handleDragOver(e, p.blockRow, iSlot),
          onDragLeave: () => p.setDragOverTarget(null),
          onDrop: (e: React.DragEvent) => p.handleDrop(e, p.blockRow, iSlot),
        })}
        isDragOver={!p.readOnly && dragRow === p.blockRow && dragSlot === iSlot}
        style={{
          gridColumn: `${iSlot} / span ${item.span}`,
          gridRow: `${p.cssRow} / span ${itemRowSpan}`,
        }}
      >
        {!p.readOnly && (
          <SlotWidgetHeader>
            <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
            <Typography
              variant="caption"
              fontWeight={600}
              noWrap
              sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {item.widget.config.title || item.widget.type}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                p.setWidgetMenu({
                  anchorEl: e.currentTarget,
                  row: p.blockRow,
                  slot: iSlot,
                });
              }}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ flexShrink: 0, p: 0.25 }}
            >
              <MoreHorizIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </SlotWidgetHeader>
        )}
        <SlotContent>
          <WidgetRenderer
            widgetId={item.widget.id}
            config={item.widget}
            width={item.span}
            height={itemRowSpan}
            blockId={p.blockId}
            row={p.blockRow}
            slot={iSlot}
          />
        </SlotContent>
        {!p.readOnly && (
          <>
            <ResizeEdge
              draggable
              onDragStart={(e) => p.handleResizeDragStart(e, p.blockRow, iSlot)}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <WidgetRowResizeHandle
              rowSpan={itemRowSpan}
              onResize={(newRowSpan) =>
                p.updateWidgetRowSpan(p.blockId, p.blockRow, iSlot, newRowSpan)
              }
            />
          </>
        )}
      </WidgetSlotCell>
    );
  }
  return cells;
}

type PlaceholderCellsParams = {
  blockRow: number;
  cssRow: number;
  blockCols: number;
  blockId: string;
  coveredCols: Set<number>;
  dragOverTarget: { row: number; slot: Slot } | null;
  pendingSlot: { blockId: string; row: number; slot: Slot } | null;
  blockPrimaryAssigned: { value: boolean };
  handleDragOver: (e: React.DragEvent, row: number, slot: Slot) => void;
  setDragOverTarget: (t: { row: number; slot: Slot } | null) => void;
  handleDrop: (e: React.DragEvent, row: number, slot: Slot) => void;
  setPaletteTarget: (v: { row: number; slot: Slot } | null) => void;
};

function buildPlaceholderCells(cells: React.ReactNode[], p: PlaceholderCellsParams): void {
  const dragRow = p.dragOverTarget?.row;
  const dragSlot = p.dragOverTarget?.slot;
  const pendingBlockId = p.pendingSlot?.blockId;
  const pendingRow = p.pendingSlot?.row;
  const pendingSlotVal = p.pendingSlot?.slot;

  for (let col = 1; col <= p.blockCols; col++) {
    if (p.coveredCols.has(col)) continue;
    const isPrimaryEmpty = !p.blockPrimaryAssigned.value;
    p.blockPrimaryAssigned.value = true;
    cells.push(
      <SlotPlaceholder
        key={`${p.blockRow}-${col}`}
        isDragOver={dragRow === p.blockRow && dragSlot === col}
        isPrimary={isPrimaryEmpty}
        isPendingTarget={pendingBlockId === p.blockId && pendingRow === p.blockRow && pendingSlotVal === col}
        onDragOver={(e) => p.handleDragOver(e, p.blockRow, col)}
        onDragLeave={() => p.setDragOverTarget(null)}
        onDrop={(e) => p.handleDrop(e, p.blockRow, col)}
        onClick={
          isPrimaryEmpty ? () => p.setPaletteTarget({ row: p.blockRow, slot: col }) : undefined
        }
        style={{ gridColumn: col, gridRow: p.cssRow }}
      >
        {isPrimaryEmpty && (
          <>
            <AddCircleOutlineIcon sx={{ fontSize: 28, color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Add Widget
            </Typography>
          </>
        )}
      </SlotPlaceholder>
    );
  }
}

// --- Component ----------------------------------------------------------------

/**
 * WidgetCardShell — full-width block shell that renders a multi-row 3-column slot grid.
 *
 * Rows are created automatically: when all 3 columns in the last row are filled,
 * a new empty row is appended at the bottom.
 *
 * Users can:
 *   - Click any empty slot to open the widget palette and add a widget there
 *   - Drag a widget card to any slot in any row/block to re-position it
 *   - Drag the <-> resize handle on the right edge of a widget to expand its column span
 *     (widgets that would be displaced are pushed to the next row rather than deleted)
 *   - Expand / shrink a widget's span via its actions menu
 *   - Configure a widget to open the sidebar
 */
export const WidgetCardShell = memo(function WidgetCardShell({
  blockId,
  title,
  hideTitle,
  blockCols,
  collapsed,
  onToggleCollapse,
  readOnly = false,
  collapsible = false,
}: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const { registry } = useWidgetRegistry();
  const {
    removeBlock,
    updateBlockTitle,
    toggleBlockHideTitle,
    toggleBlockCollapsible,
    deleteEmptyRow,
    addSection,
    removeWidgetAtSlot,
    moveWidget,
    moveWidgetFromSectionToBlock,
    moveSection,
    updateWidgetSpan,
    updateWidgetRowSpan,
    updateSectionItemSpan,
    assignWidgetToSlot,
  } = useDashboardBuilderEditing();
  const palette = useBuilderPalette();
  const {
    setSelectedBlockId,
    setSidebarPanel,
    setSelectedWidgetId,
    setPendingSlot,
    setPendingWidgetType,
    selectedWidgetId,
    pendingSlot,
  } = useDashboardBuilder();

  const block = config.blocks.find((b) => b.id === blockId);
  const items = block?.items ?? [];

  // Build a height-map: Map<blockRowIndex, heightUnits>.
  // In preview/readOnly, use the preview variant which strips trailing empty rows
  // and minInnerRows padding from sections — those exist only for drag-drop in the builder.
  const heightMap = useMemo(
    () => (readOnly ? getPreviewBlockRowHeightMap(items, blockCols) : getBlockRowHeightMap(items, blockCols)),
    [items, readOnly, blockCols]
  );

  // Layout computations extracted into useMemo to isolate branching logic.
  const { totalHeight, maxRow, sectionAtMaxRow, multiRowWidgetAtMaxRow, trailingRowIdx, rows } =
    useMemo(() => {
      const contentHeight = readOnly ? getPreviewContentHeightUnits(items, blockCols) : getContentHeightUnits(items, blockCols);
      const totalHeight = readOnly ? contentHeight : Math.max(block?.minRows ?? 1, contentHeight);
      const extraFlex = totalHeight - contentHeight;
      const maxRow = getMaxRow(items);
      const sectionAtMaxRow =
        items.find(
          (i): i is Extract<BlockItem, { type: 'section' }> => (i.row ?? 1) === maxRow && i.type === 'section'
        ) ?? null;
      const lastRowSectionHeight = sectionAtMaxRow ? (heightMap.get(maxRow) ?? 1) : 0;
      const multiRowWidgetAtMaxRow =
        items.find(
          (i): i is Extract<BlockItem, { type: 'widget' }> =>
            (i.row ?? 1) === maxRow && i.type === 'widget' && (i.rowSpan ?? 1) > 1
        ) ?? null;
      const lastRowWidgetRowSpan = multiRowWidgetAtMaxRow?.rowSpan ?? 1;
      let trailingRowIdx: number;
      if (items.length === 0) {
        trailingRowIdx = 1;
      } else if (sectionAtMaxRow) {
        trailingRowIdx = maxRow + lastRowSectionHeight;
      } else if (multiRowWidgetAtMaxRow) {
        trailingRowIdx = maxRow + lastRowWidgetRowSpan;
      } else if (isRowFull(items, maxRow, blockCols)) {
        trailingRowIdx = maxRow + 1;
      } else {
        trailingRowIdx = maxRow;
      }
      const rows = Array.from({ length: trailingRowIdx + extraFlex }, (_, i) => i + 1);
      return { totalHeight, maxRow, sectionAtMaxRow, multiRowWidgetAtMaxRow, trailingRowIdx, rows };
    }, [readOnly, items, block?.minRows, blockCols, heightMap]);

  // Group block rows for rendering.
  // A section OR a multi-row widget absorbs the next (N-1) successive block rows into its
  // CSS grid, giving each sub-row its own independent column of widget/placeholder slots.
  // This lets users place a 1×1 widget beside the top AND beside the bottom of a tall item.
  const renderedGroups = useMemo(() => {
    const groups: Array<{
      primaryRow: number;
      blockRows: number[]; // [primaryRow, …, primaryRow + groupRows - 1]
      flexSpan: number; // flex weight for this group's shell container
      rowCount: number; // CSS grid rows inside this group
      // Every section starting on this row — up to 3 can sit side by side (e.g. converted
      // legacy segment dashboards); each spans the group's full height.
      sectionItems: Array<Extract<BlockItem, { type: 'section' }>>;
      sectionRows: number;
    }> = [];
    const absorbed = new Set<number>();

    for (const row of rows) {
      if (absorbed.has(row)) continue;
      const rowSections = items.filter(
        (i): i is Extract<BlockItem, { type: 'section' }> => (i.row ?? 1) === row && i.type === 'section'
      );
      // heightMap already takes the tallest section in the row.
      const sectionRows = rowSections.length > 0 ? (heightMap.get(row) ?? 1) : 1;
      // Multi-row span from any widget in this primary row (max rowSpan)
      const widgetRows = items
        .filter(
          (i): i is Extract<BlockItem, { type: 'widget' }> => i.type === 'widget' && (i.row ?? 1) === row
        )
        .reduce((m, i) => Math.max(m, i.rowSpan ?? 1), 1);
      const groupRows = Math.max(sectionRows, widgetRows);

      const blockRows: number[] = [row];
      for (let k = 1; k < groupRows; k++) {
        absorbed.add(row + k);
        blockRows.push(row + k);
      }
      groups.push({
        primaryRow: row,
        blockRows,
        flexSpan: groupRows,
        rowCount: groupRows,
        sectionItems: rowSections,
        sectionRows,
      });
    }
    return groups;
  }, [rows, items, heightMap]);

  // -- Title ------------------------------------------------------------------
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => updateBlockTitle(blockId, e.target.value),
    [updateBlockTitle, blockId]
  );

  // -- Block menu -------------------------------------------------------------
  const blockMenuId = useId();
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<null | HTMLElement>(null);

  // -- Per-widget menu --------------------------------------------------------
  const widgetMenuId = useId();
  const [widgetMenu, setWidgetMenu] = useState<{ anchorEl: HTMLElement; row: number; slot: Slot } | null>(
    null
  );
  const widgetMenuBw = useMemo(() => {
    if (!widgetMenu) return null;
    return (
      items.find(
        (item): item is BlockWidgetItem =>
          item.type === 'widget' && item.slot === widgetMenu.slot && (item.row ?? 1) === widgetMenu.row
      ) ?? null
    );
  }, [widgetMenu, items]);

  const menuActions = useMemo(
    () => ({
      canExpand: !!(widgetMenuBw && widgetMenuBw.span < (blockCols + 1) - widgetMenuBw.slot),
      canShrink: !!(widgetMenuBw && widgetMenuBw.span > 1),
      canExpandDown: !!(widgetMenuBw && (widgetMenuBw.rowSpan ?? 1) < MAX_PERMISSIBLE_ROW_SPAN),
      canShrinkUp: !!(widgetMenuBw && (widgetMenuBw.rowSpan ?? 1) > 1),
    }),
    [widgetMenuBw, blockCols]
  );

  // collapsed / onToggleCollapse are received as props — BuilderCanvas controls
  // the actual RGL item height so it shrinks/expands in the layout.

  // -- Palette ----------------------------------------------------------------
  const [paletteTarget, setPaletteTarget] = useState<{ row: number; slot: Slot } | null>(null);

  // Track whether the block-level primary "Add Widget" placeholder has been assigned.
  // Declared here so it survives across the renderedGroups.map callbacks, which execute
  // synchronously during render — safe to use a mutable variable.
  const blockPrimaryAssigned = { value: false };

  // Track which empty group is being hovered for delete — drives the blue tint
  const [deleteHoverRow, setDeleteHoverRow] = useState<number | null>(null);

  // -- Drag & drop ------------------------------------------------------------
  const [dragOverTarget, setDragOverTarget] = useState<{ row: number; slot: Slot } | null>(null);

  const handleMoveDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      const data: DragData = { kind: 'move', blockId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
    },
    [blockId]
  );

  const handleResizeDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      e.stopPropagation();
      const data: DragData = { kind: 'resize', blockId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId]
  );

  const handleSectionResizeDragStart = useCallback(
    (e: React.DragEvent, sectionId: string, row: number, slot: Slot) => {
      e.stopPropagation();
      const data: DragData = { kind: 'section-resize', blockId, sectionId, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId]
  );

  const handleDragOver = useCallback((e: React.DragEvent, row: number, slot: Slot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ row, slot });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toRow: number, toSlot: Slot) => {
      e.preventDefault();
      setDragOverTarget(null);
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain')) as DragData;
        if (data.kind === 'move') {
          moveWidget(data.blockId, data.row, data.slot, blockId, toRow, toSlot);
        } else if (data.kind === 'section-widget-move') {
          // Widget dragged from inside a section — drop it into this block slot
          moveWidgetFromSectionToBlock(
            data.blockId,
            data.sectionId,
            data.row,
            data.slot,
            blockId,
            toRow,
            toSlot
          );
        } else if (data.kind === 'section-move') {
          if (data.blockId === blockId) {
            moveSection(blockId, data.row, data.slot, toRow, toSlot);
          }
        } else if (data.kind === 'section-resize') {
          if (data.blockId === blockId && data.row === toRow && toSlot >= data.slot) {
            const newSpan = Math.min(blockCols, Math.max(1, toSlot - data.slot + 1));
            updateSectionItemSpan(blockId, data.sectionId, newSpan);
          }
        } else if (data.kind === 'resize') {
          // Expand (drop to the right) or shrink (drop on own slot or within span)
          if (data.blockId === blockId && data.row === toRow && toSlot >= data.slot) {
            const newSpan = Math.min(blockCols, Math.max(1, toSlot - data.slot + 1));
            updateWidgetSpan(blockId, data.row, data.slot, newSpan);
          }
        } else { /* noop */ }
      } catch {
        /* ignore malformed drag data */
      }
    },
    [blockId, moveWidget, moveWidgetFromSectionToBlock, moveSection, updateWidgetSpan, updateSectionItemSpan]
  );

  const handleConfigure = useCallback(
    (row: number, slot: Slot) => {
      const foundItem = items.find(
        (item): item is BlockWidgetItem =>
          item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row
      );
      if (!foundItem) return;
      setSelectedBlockId(blockId);
      setSelectedWidgetId(foundItem.widget.id);
      setSidebarPanel('chart-config');
      setWidgetMenu(null);
    },
    [items, blockId, setSelectedBlockId, setSelectedWidgetId, setSidebarPanel]
  );

  return (
    <Shell>
      {/* --- Block header --- */}
      <BlockHeader
        readOnly={readOnly}
        title={title}
        hideTitle={hideTitle}
        collapsible={collapsible}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        handleTitleChange={handleTitleChange}
        blockMenuAnchor={blockMenuAnchor}
        setBlockMenuAnchor={setBlockMenuAnchor}
        blockMenuId={blockMenuId}
        blockId={blockId}
        toggleBlockHideTitle={toggleBlockHideTitle}
        toggleBlockCollapsible={toggleBlockCollapsible}
        removeBlock={removeBlock}
      />

      {/* --- Rows --- */}
      {!collapsed &&
        renderedGroups.map(({ primaryRow, blockRows, flexSpan, rowCount, sectionItems, sectionRows }) => {
          // Columns physically covered by the row's sections across every sub-row
          const secCols = new Set<number>();
          for (const sectionItem of sectionItems) {
            for (let c = 0; c < sectionItem.span; c++) secCols.add(sectionItem.slot + c);
          }

          // A group is empty when it has no section and no items in any of its block rows
          const isGroupEmpty =
            sectionItems.length === 0 &&
            blockRows.every((blockRow) => items.filter((i) => (i.row ?? 1) === blockRow).length === 0);

          // In read-only (renderer) mode, skip empty rows entirely — no placeholders to show.
          if (readOnly && isGroupEmpty) return null;

          return (
            <SlotGrid
              key={primaryRow}
              flexSpan={flexSpan}
              rowCount={rowCount}
              cols={blockCols}
              readOnly={readOnly}
              deleteHovering={isGroupEmpty && deleteHoverRow === primaryRow}
            >
              {/* Each section spans all sub-rows via explicit CSS grid placement */}
              {sectionItems.map((sectionItem) => {
                  const sSlot = sectionItem.slot;
                  return (
                    <SectionSlotCell
                      key={`section-${primaryRow}-${sSlot}`}
                      span={sectionItem.span}
                      style={{
                        gridColumn: `${sSlot} / span ${sectionItem.span}`,
                        gridRow: `1 / span ${sectionRows}`,
                      }}
                      {...(!readOnly && {
                        onDragOver: (e: React.DragEvent) => handleDragOver(e, primaryRow, sSlot),
                        onDragLeave: () => setDragOverTarget(null),
                        onDrop: (e: React.DragEvent) => handleDrop(e, primaryRow, sSlot),
                      })}
                    >
                      <SectionCardShell
                        blockId={blockId}
                        section={sectionItem.section}
                        sectionItemSpan={sectionItem.span}
                        sectionItemRow={primaryRow}
                        sectionItemSlot={sSlot}
                        readOnly={readOnly}
                      />
                      {!readOnly && (
                        <SectionResizeEdge
                          draggable
                          onDragStart={(e) =>
                            handleSectionResizeDragStart(e, sectionItem.section.id, primaryRow, sSlot)
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      )}
                    </SectionSlotCell>
                  );
                })}

              {/* Each sub-row gets independent widget/placeholder cells */}
              {blockRows.flatMap((blockRow, subRowIdx) =>
                buildSubRowCells({
                  blockRow,
                  subRowIdx,
                  primaryRow,
                  secCols,
                  items,
                  readOnly,
                  blockCols,
                  blockId,
                  registry,
                  selectedWidgetId,
                  dragOverTarget,
                  pendingSlot,
                  blockPrimaryAssigned,
                  handleMoveDragStart,
                  handleDragOver,
                  setDragOverTarget,
                  handleDrop,
                  handleResizeDragStart,
                  updateWidgetRowSpan,
                  setWidgetMenu,
                  setPaletteTarget,
                })
              )}

              {/* Delete-row button — bottom-center of the empty row.
                Hidden in preview mode and when the row is the auto-generated trailing row. */}
              {!readOnly &&
                isGroupEmpty &&
                totalHeight > 1 &&
                !(
                  primaryRow === trailingRowIdx &&
                  (sectionAtMaxRow !== null ||
                    multiRowWidgetAtMaxRow !== null ||
                    isRowFull(items, maxRow, blockCols))
                ) && (
                  <DeleteRowButton
                    size="small"
                    onClick={() => deleteEmptyRow(blockId, primaryRow)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => setDeleteHoverRow(primaryRow)}
                    onMouseLeave={() => setDeleteHoverRow(null)}
                    aria-label="Remove empty row"
                    title="Remove row"
                  >
                    <RemoveCircleOutlineIcon sx={{ fontSize: 22 }} />
                  </DeleteRowButton>
                )}
            </SlotGrid>
          );
        })}

      {/* --- Widget actions menu --- */}
      {!readOnly && (
        <ThemedMenu
          id={widgetMenuId}
          anchorEl={widgetMenu?.anchorEl}
          open={Boolean(widgetMenu)}
          onClose={() => setWidgetMenu(null)}
          MenuListProps={{ dense: true }}
        >
          <MenuItem onClick={() => widgetMenu && handleConfigure(widgetMenu.row, widgetMenu.slot)}>
            Edit widget
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!widgetMenu) return;
              removeWidgetAtSlot(blockId, widgetMenu.row, widgetMenu.slot);
              setWidgetMenu(null);
            }}
          >
            Remove widget
          </MenuItem>
          {menuActions.canExpand && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetSpan(blockId, widgetMenu.row, widgetMenu.slot, widgetMenuBw.span + 1);
                setWidgetMenu(null);
              }}
            >
              {`Expand \u2192`}
            </MenuItem>
          )}
          {menuActions.canShrink && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetSpan(blockId, widgetMenu.row, widgetMenu.slot, widgetMenuBw.span - 1);
                setWidgetMenu(null);
              }}
            >
              {`\u2190 Shrink`}
            </MenuItem>
          )}
          {menuActions.canExpandDown && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetRowSpan(
                  blockId,
                  widgetMenu.row,
                  widgetMenu.slot,
                  (widgetMenuBw.rowSpan ?? 1) + 1
                );
                setWidgetMenu(null);
              }}
            >
              Expand down
            </MenuItem>
          )}
          {menuActions.canShrinkUp && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateWidgetRowSpan(
                  blockId,
                  widgetMenu.row,
                  widgetMenu.slot,
                  (widgetMenuBw.rowSpan ?? 1) - 1
                );
                setWidgetMenu(null);
              }}
            >
              Shrink up
            </MenuItem>
          )}
        </ThemedMenu>
      )}

      {/* --- Widget palette (row + slot targeted) --- */}
      {!readOnly && (
        <WidgetPalette
          open={paletteTarget !== null}
          onClose={() => setPaletteTarget(null)}
          onSelect={(type) => {
            if (!paletteTarget) return;
            if (type === 'section') {
              addSection(blockId, paletteTarget.row, paletteTarget.slot, 1);
              setPaletteTarget(null);
            } else {
              setPendingSlot({ blockId, row: paletteTarget.row, slot: paletteTarget.slot });
              setPendingWidgetType(type);
              setSidebarPanel('create-widget');
              setPaletteTarget(null);
            }
          }}
          onSelectLibraryWidget={(widget) => {
            if (!paletteTarget || !palette) return;
            const { type, config: widgetConfig } = palette.toPageWidget(widget);
            assignWidgetToSlot(blockId, paletteTarget.row, paletteTarget.slot, type, widgetConfig);
            setPaletteTarget(null);
          }}
        />
      )}
    </Shell>
  );
});

// --- WidgetRowResizeHandle — bottom-edge pointer-capture resize for row span -----------------

type WidgetRowResizeHandleProps = {
  rowSpan: number;
  onResize: (newRowSpan: number) => void;
};

/** Absolutely-positioned bottom strip on a widget cell that lets the user drag to change rowSpan. */
function WidgetRowResizeHandle({ rowSpan, onResize }: WidgetRowResizeHandleProps): React.ReactElement {
  const start = useRef<{ y: number; rows: number } | null>(null);
  return (
    <RowResizeEdge
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { y: e.clientY, rows: rowSpan };
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const delta = Math.round((e.clientY - start.current.y) / ROW_HEIGHT);
        const newRowSpan = Math.max(1, Math.min(MAX_PERMISSIBLE_ROW_SPAN, start.current.rows + delta));
        onResize(newRowSpan);
      }}
      onPointerUp={(e) => {
        if (!start.current) return;
        const delta = Math.round((e.clientY - start.current.y) / ROW_HEIGHT);
        const newRowSpan = Math.max(1, Math.min(MAX_PERMISSIBLE_ROW_SPAN, start.current.rows + delta));
        onResize(newRowSpan);
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
    />
  );
}

// --- WidgetRenderer — resolves data from context and delegates to the registry render fn -----

type WidgetRendererProps = {
  widgetId: string;
  config: WidgetConfig;
  width: number;
  height: number;
  blockId: string;
  row: number;
  slot: Slot;
};

function WidgetRenderer({
  widgetId,
  config,
  width,
  height,
  blockId,
  row,
  slot,
}: WidgetRendererProps): React.ReactElement {
  const { registry } = useWidgetRegistry();
  const { updateWidgetRowSpan } = useDashboardBuilderEditing();
  const { data, isLoading } = useDashboardWidgetData(widgetId);
  const entry = registry.get(config.type);
  const shouldAutoSizeRepDashboard = config.type === 'rep_dashboard';

  useEffect(() => {
    if (!entry) return;
    if (!shouldAutoSizeRepDashboard) return;
    if (data === null || data.type !== 'rep_dashboard' || isLoading) return;

    const targetRowSpan = Math.max(1, entry.autoRowSpan?.(data) ?? 1);
    if (targetRowSpan !== height) {
      const delta = targetRowSpan - height;
      const nextRowSpan = height + Math.sign(delta);
      updateWidgetRowSpan(blockId, row, slot, nextRowSpan);
    }
  }, [blockId, data, entry, height, isLoading, row, slot, shouldAutoSizeRepDashboard, updateWidgetRowSpan]);

  if (!entry) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
        Unknown widget type: {config.type}
      </Typography>
    );
  }

  return entry.render({ widgetId, config, data, width, height, isLoading });
}

// --- Styled components --------------------------------------------------------

const Shell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Fill the RGL item but leave BLOCK_GAP (12 px) at the bottom as a visual gap
  // between blocks.  The RGL item height always bakes in that gap.
  height: `calc(100% - ${BLOCK_GAP}px)`,
  width: '100%',
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: '12px',
  background: theme.palette.white.main,
  overflow: 'hidden',
}));

const ShellHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'hideTitle',
})<{ collapsed: boolean; hideTitle: boolean }>(({ theme, collapsed, hideTitle }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
  flexShrink: 0,
  ...(hideTitle
    ? {
        padding: '2px 4px 2px 8px',
        height: '24px',
        backgroundColor: `${theme.palette.primary.main}18`,
        backdropFilter: 'blur(4px)',
        borderBottom: `1px solid ${theme.palette.primary.light1}`,
      }
    : {
        padding: '14px 8px 8px 12px',
        borderBottom: collapsed ? 'none' : `1px solid ${theme.palette.gray.light2}`,
        minHeight: '40px',
      }),
}));

const TitleInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  flex: 1,
  ...(hideTitle
    ? {
        marginRight: '26px',
        '& input': {
          padding: 0,
          cursor: 'text',
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 700,
          color: theme.palette.text.primary,
          lineHeight: 1,
        },
      }
    : {
        borderBottom: `2px solid ${theme.palette.text.disabled}`,
        '& input': {
          padding: 0,
          cursor: 'text',
          fontSize: theme.typography.h6.fontSize,
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      }),
}));

const SlotGrid = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'flexSpan' &&
    prop !== 'rowCount' &&
    prop !== 'cols' &&
    prop !== 'deleteHovering' &&
    prop !== 'readOnly',
})<{ flexSpan: number; rowCount: number; cols: number; deleteHovering?: boolean; readOnly?: boolean }>(
  ({ theme, flexSpan, rowCount, cols, deleteHovering, readOnly }) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    // Use 1fr rows so grid tracks grow to fill available height when the block is stretched.
    // minHeight ensures a minimum usable size even when the block is at its default height.
    gridTemplateRows: `repeat(${rowCount}, 1fr)`,
    flex: flexSpan,
    minHeight: `${flexSpan * ROW_HEIGHT}px`,
    minWidth: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: deleteHovering ? `${theme.palette.primary.main}0E` : 'transparent',
    transition: 'background-color 150ms',
    '& + &': {
      paddingTop: 0,
      ...(!readOnly && {
        borderTop: `1px dashed ${theme.palette.divider}`,
      }),
    },
  })
);

const WidgetSlotCell = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'span' &&
    prop !== 'isDragOver' &&
    prop !== 'readOnly' &&
    prop !== 'isEditing' &&
    prop !== 'noBorder',
})<{ span: number; isDragOver: boolean; readOnly?: boolean; isEditing?: boolean; noBorder?: boolean }>(({
  theme,
  isDragOver,
  isEditing,
  noBorder,
}) => {
  let borderStyle: string;
  if (noBorder) {
    borderStyle = 'none';
  } else if (isEditing) {
    borderStyle = `2px solid ${theme.palette.primary.main}`;
  } else {
    borderStyle = `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`;
  }

  return {
    // gridColumn and gridRow are set via inline style for precise sub-row placement.
    // Let the CSS grid track control the height (1fr rows in SlotGrid).
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    border: borderStyle,
    borderRadius: noBorder ? 0 : '12px',
    background: theme.palette.background.paper,
    transition: 'border-color 150ms',
    position: 'relative',
    ...(isEditing && {
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}30`,
    }),
  };
});

/** Container cell for a section item — gridColumn/gridRow set via inline style for sub-row placement */
const SectionSlotCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'span',
})<{ span: number }>(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  position: 'relative',
}));

/**
 * Invisible full-height right-edge zone on a section cell. Shows an ew-resize cursor on hover.
 * Drag it to a further slot to expand the section's column span in the parent block.
 */
const SectionResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '10px',
  height: '100%',
  cursor: 'ew-resize',
  zIndex: 10,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset -3px 0 0 0 ${theme.palette.primary.main}`,
  },
}));

const SlotWidgetHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '28px',
  flexShrink: 0,
}));

const SlotContent = styled('div')({
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

/**
 * Invisible full-height right-edge zone. Shows an ew-resize cursor on hover
 * and a subtle blue highlight so users know they can drag to resize the widget.
 */
const ResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '10px',
  height: '100%',
  cursor: 'ew-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset -3px 0 0 0 ${theme.palette.primary.main}`,
  },
}));

/**
 * Invisible full-width bottom-edge zone. Shows an s-resize cursor on hover
 * and a subtle blue highlight so users know they can drag to resize the widget vertically.
 */
const RowResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '10px',
  cursor: 's-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset 0 -3px 0 0 ${theme.palette.primary.main}`,
  },
}));

// Circled-minus button anchored to the bottom-center edge of an empty SlotGrid row
const DeleteRowButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: -7,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 3,
  padding: 0,
  // White backing so it looks like it sits on the border
  backgroundColor: theme.palette.white?.main ?? '#fff',
  borderRadius: '50%',
  color: theme.palette.primary.main,
  transition: 'color 150ms',
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.white?.main ?? '#fff',
  },
}));

const SlotPlaceholder = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDragOver' && prop !== 'isPrimary' && prop !== 'isPendingTarget',
})<{ isDragOver: boolean; isPrimary: boolean; isPendingTarget?: boolean }>(
  ({ theme, isDragOver, isPrimary, isPendingTarget }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // No fixed height — let the CSS grid track determine height (same as WidgetSlotCell).
    minHeight: `${ROW_HEIGHT - 2 * SLOT_PADDING}px`,
    border: isPendingTarget
      ? `2px solid ${theme.palette.primary.main}`
      : `1.5px ${isPrimary ? 'solid' : 'dashed'} ${isDragOver ? theme.palette.primary.main : theme.palette.gray.light2}`,
    borderRadius: '12px',
    cursor: isPrimary ? 'pointer' : 'default',
    transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
    backgroundColor: isDragOver ? theme.palette.primary.light4 : 'transparent',
    ...(isPendingTarget && {
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}30`,
    }),
    ...(isPrimary &&
      !isPendingTarget && {
        '&:hover': {
          borderColor: theme.palette.gray.light1,
          backgroundColor: theme.palette.gray.light4,
        },
      }),
  })
);

/** Clean block-title header rendered in preview mode (no border-bottom, no editing chrome). */
const PreviewBlockHeader = styled('div')(({ theme }) => ({
  padding: '10px 16px 6px',
  flexShrink: 0,
}));
